import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { beneficiaryDesignationsService, Beneficiary } from '../../../../services/beneficiaryDesignationsInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('BeneficiaryDesignationsInput');

type BeneficiaryDesignationsInputScreenRouteProp = RouteProp<RootStackParamList, 'BeneficiaryDesignationsInput'>;

const ACCOUNT_TYPES = [
  { key: 'lifeInsurance', label: 'Life Insurance' },
  { key: 'retirement', label: 'Retirement Accounts (401k, IRA)' },
  { key: 'bank', label: 'Bank Accounts (POD)' },
  { key: 'investment', label: 'Investment Accounts (TOD)' },
  { key: 'other', label: 'Other Assets' },
];

export const BeneficiaryDesignationsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<BeneficiaryDesignationsInputScreenRouteProp>();

  const { beneficiaryDesignationsData, isEditing } = route.params || {};

  const [selectedAccountType, setSelectedAccountType] = useState<string>('lifeInsurance');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [beneficiaryRelationship, setBeneficiaryRelationship] = useState('');
  const [beneficiaryPercentage, setBeneficiaryPercentage] = useState('');
  const [policyOrAccountId, setPolicyOrAccountId] = useState('');
  const [institutionName, setInstitutionName] = useState('');

  const [lifeInsuranceBeneficiaries, setLifeInsuranceBeneficiaries] = useState<Beneficiary[]>(
    beneficiaryDesignationsData?.lifeInsuranceBeneficiaries || []
  );
  const [retirementBeneficiaries, setRetirementBeneficiaries] = useState<Beneficiary[]>(
    beneficiaryDesignationsData?.retirementAccountBeneficiaries || []
  );
  const [bankBeneficiaries, setBankBeneficiaries] = useState<Beneficiary[]>(
    beneficiaryDesignationsData?.bankAccountBeneficiaries || []
  );
  const [investmentBeneficiaries, setInvestmentBeneficiaries] = useState<Beneficiary[]>(
    beneficiaryDesignationsData?.investmentAccountBeneficiaries || []
  );
  const [otherBeneficiaries, setOtherBeneficiaries] = useState<Beneficiary[]>(
    beneficiaryDesignationsData?.otherBeneficiaries || []
  );

  const [lastReviewedDate, setLastReviewedDate] = useState(beneficiaryDesignationsData?.lastReviewedDate || '');
  const [additionalNotes, setAdditionalNotes] = useState(beneficiaryDesignationsData?.additionalNotes || '');
  const [saving, setSaving] = useState(false);

  // Date formatting helper for MM/DD/YYYY
  const formatDateInput = (text: string, setter: (value: string) => void) => {
    // Remove non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format as MM/DD/YYYY
    let formatted = '';
    if (cleaned.length > 0) {
      formatted = cleaned.substring(0, 2);
      if (cleaned.length > 2) {
        formatted += '/' + cleaned.substring(2, 4);
        if (cleaned.length > 4) {
          formatted += '/' + cleaned.substring(4, 8);
        }
      }
    }

    setter(formatted);
  };

  const addBeneficiary = () => {
    if (!beneficiaryName.trim()) {
      Alert.alert('Error', 'Please enter the beneficiary name');
      return;
    }

    const newBeneficiary: Beneficiary = {
      name: beneficiaryName.trim(),
      relationship: beneficiaryRelationship.trim(),
      percentage: beneficiaryPercentage ? parseInt(beneficiaryPercentage, 10) : undefined,
      policyId: policyOrAccountId.trim() || undefined,
      bankName: institutionName.trim() || undefined,
      accountType: selectedAccountType,
    };

    switch (selectedAccountType) {
      case 'lifeInsurance':
        setLifeInsuranceBeneficiaries([...lifeInsuranceBeneficiaries, newBeneficiary]);
        break;
      case 'retirement':
        setRetirementBeneficiaries([...retirementBeneficiaries, newBeneficiary]);
        break;
      case 'bank':
        setBankBeneficiaries([...bankBeneficiaries, newBeneficiary]);
        break;
      case 'investment':
        setInvestmentBeneficiaries([...investmentBeneficiaries, newBeneficiary]);
        break;
      case 'other':
        setOtherBeneficiaries([...otherBeneficiaries, newBeneficiary]);
        break;
    }

    // Clear input fields
    setBeneficiaryName('');
    setBeneficiaryRelationship('');
    setBeneficiaryPercentage('');
    setPolicyOrAccountId('');
    setInstitutionName('');
  };

  const removeBeneficiary = (type: string, index: number) => {
    switch (type) {
      case 'lifeInsurance':
        setLifeInsuranceBeneficiaries(lifeInsuranceBeneficiaries.filter((_, i) => i !== index));
        break;
      case 'retirement':
        setRetirementBeneficiaries(retirementBeneficiaries.filter((_, i) => i !== index));
        break;
      case 'bank':
        setBankBeneficiaries(bankBeneficiaries.filter((_, i) => i !== index));
        break;
      case 'investment':
        setInvestmentBeneficiaries(investmentBeneficiaries.filter((_, i) => i !== index));
        break;
      case 'other':
        setOtherBeneficiaries(otherBeneficiaries.filter((_, i) => i !== index));
        break;
    }
  };

  const getTotalBeneficiaries = () => {
    return lifeInsuranceBeneficiaries.length + retirementBeneficiaries.length +
           bankBeneficiaries.length + investmentBeneficiaries.length + otherBeneficiaries.length;
  };

  const handleSave = async () => {
    if (getTotalBeneficiaries() === 0) {
      Alert.alert('Error', 'Please add at least one beneficiary');
      return;
    }

    try {
      setSaving(true);

      const data = {
        lifeInsuranceBeneficiaries,
        retirementAccountBeneficiaries: retirementBeneficiaries,
        bankAccountBeneficiaries: bankBeneficiaries,
        investmentAccountBeneficiaries: investmentBeneficiaries,
        otherBeneficiaries,
        lastReviewedDate: lastReviewedDate.trim(),
        additionalNotes: additionalNotes.trim(),
      };

      let savedData;
      if (isEditing && beneficiaryDesignationsData?.id) {
        savedData = await beneficiaryDesignationsService.updateBeneficiaryDesignations(beneficiaryDesignationsData.id, data);
        const updatedData = savedData?.data || savedData;
        logger.debug('Beneficiary designations updated successfully with ID:', updatedData.id);
        navigation.navigate('BeneficiaryDesignationsLockUnlock', { beneficiaryDesignationsId: updatedData.id });
      } else {
        savedData = await beneficiaryDesignationsService.createBeneficiaryDesignations(data);
        const createdData = savedData?.data || savedData;
        logger.debug('Beneficiary designations saved successfully with ID:', createdData.id);
        navigation.navigate('BeneficiaryDesignationsLockUnlock', { beneficiaryDesignationsId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving beneficiary designations:', error);
      Alert.alert('Error', 'Failed to save beneficiary designations. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderBeneficiaryList = (beneficiaries: Beneficiary[], type: string, label: string) => {
    if (beneficiaries.length === 0) return null;
    return (
      <View style={styles.beneficiarySection}>
        <Text style={styles.beneficiarySectionTitle}>{label}</Text>
        {beneficiaries.map((b, index) => (
          <View key={index} style={styles.beneficiaryItem}>
            <View style={styles.beneficiaryInfo}>
              <Text style={styles.beneficiaryName}>{b.name}</Text>
              <Text style={styles.beneficiaryDetails}>
                {b.relationship}{b.percentage ? ` - ${b.percentage}%` : ''}
              </Text>
            </View>
            <TouchableOpacity onPress={() => removeBeneficiary(type, index)}>
              <Text style={styles.removeButton}>Remove</Text>
            </TouchableOpacity>
          </View>
        ))}
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Image source={require('../../../../assets/icons/back.png')} style={[styles.backIcon, { tintColor: '#fff' }]} />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image source={require('../../../../assets/icons/keyimage.png')} style={styles.backgroundKey} />
        <Image source={require('../../../../assets/logo.png')} style={styles.logo} resizeMode="contain" />
        <Text style={styles.title}>Add Beneficiary Details</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <Text style={styles.sectionTitle}>Account Type:</Text>
          <View style={styles.accountTypeContainer}>
            {ACCOUNT_TYPES.map((type) => (
              <TouchableOpacity
                key={type.key}
                style={[styles.accountTypeButton, selectedAccountType === type.key && styles.accountTypeSelected]}
                onPress={() => setSelectedAccountType(type.key)}
              >
                <Text style={[styles.accountTypeText, selectedAccountType === type.key && styles.accountTypeTextSelected]}>
                  {type.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={styles.sectionTitle}>Add Beneficiary:</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Beneficiary Name:</Text>
            <TextInput
              style={styles.input}
              value={beneficiaryName}
              onChangeText={setBeneficiaryName}
              placeholder="Enter beneficiary's full name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship:</Text>
            <TextInput
              style={styles.input}
              value={beneficiaryRelationship}
              onChangeText={setBeneficiaryRelationship}
              placeholder="e.g., Spouse, Child"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Percentage (Optional):</Text>
            <TextInput
              style={styles.input}
              value={beneficiaryPercentage}
              onChangeText={setBeneficiaryPercentage}
              placeholder="e.g., 50"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Policy/Account ID (Optional):</Text>
            <TextInput
              style={styles.input}
              value={policyOrAccountId}
              onChangeText={setPolicyOrAccountId}
              placeholder="Enter policy or account number"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Institution Name (Optional):</Text>
            <TextInput
              style={styles.input}
              value={institutionName}
              onChangeText={setInstitutionName}
              placeholder="e.g., Fidelity, State Farm"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <TouchableOpacity style={styles.addButton} onPress={addBeneficiary}>
            <Text style={styles.addButtonText}>+ Add Beneficiary</Text>
          </TouchableOpacity>

          {getTotalBeneficiaries() > 0 && (
            <View style={styles.addedBeneficiaries}>
              <Text style={styles.sectionTitle}>Added Beneficiaries ({getTotalBeneficiaries()}):</Text>
              {renderBeneficiaryList(lifeInsuranceBeneficiaries, 'lifeInsurance', 'Life Insurance')}
              {renderBeneficiaryList(retirementBeneficiaries, 'retirement', 'Retirement Accounts')}
              {renderBeneficiaryList(bankBeneficiaries, 'bank', 'Bank Accounts')}
              {renderBeneficiaryList(investmentBeneficiaries, 'investment', 'Investment Accounts')}
              {renderBeneficiaryList(otherBeneficiaries, 'other', 'Other Assets')}
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Last Reviewed Date (Optional):</Text>
            <TextInput
              style={styles.input}
              value={lastReviewedDate}
              onChangeText={(text) => formatDateInput(text, setLastReviewedDate)}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes (Optional):</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={additionalNotes}
              onChangeText={setAdditionalNotes}
              placeholder="Any additional notes"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#29BFC0' },
  header: { flexDirection: 'row', alignItems: 'center', padding: ms(15), paddingTop: ms(25), position: 'relative' },
  backButton: { position: 'absolute', left: ms(50), padding: ms(15), paddingTop: ms(50), zIndex: 1 },
  backIcon: { width: s(32), height: s(32), tintColor: '#fff' },
  topSection: { alignItems: 'center', paddingVertical: ms(20) },
  backgroundKey: { position: 'absolute', width: '85%', height: '85%', opacity: 0.1 },
  logo: { width: s(125), height: s(125), marginBottom: ms(16) },
  title: { fontSize: ms(24, 0.3), fontWeight: 'bold', color: '#FFFFFF', marginBottom: ms(8), paddingHorizontal: ms(30), textAlign: 'center' },
  inputContainer: { flex: 1, backgroundColor: '#F3F6F8', borderTopLeftRadius: ms(20), borderTopRightRadius: ms(20), paddingTop: ms(20) },
  scrollView: { paddingHorizontal: ms(16), paddingBottom: ms(120) },
  sectionTitle: { fontSize: ms(20, 0.3), fontWeight: 'bold', color: '#1C2C4F', marginBottom: ms(16), marginTop: ms(10) },
  accountTypeContainer: { marginBottom: ms(20) },
  accountTypeButton: { backgroundColor: '#FFFFFF', borderRadius: ms(12), padding: ms(12), marginBottom: ms(8), borderWidth: ms(1), borderColor: '#E5E5E5' },
  accountTypeSelected: { borderColor: '#29BFC0', borderWidth: ms(2), backgroundColor: '#E8F8F8' },
  accountTypeText: { fontSize: ms(16, 0.3), color: '#1C2C4F' },
  accountTypeTextSelected: { color: '#29BFC0', fontWeight: '600' },
  inputGroup: { marginBottom: ms(16) },
  label: { fontSize: ms(16, 0.3), fontWeight: '600', color: '#6B7280', marginBottom: ms(8) },
  input: { backgroundColor: '#FFFFFF', borderRadius: ms(25), padding: ms(15), fontSize: ms(16, 0.3), color: '#1C2C4F', borderWidth: ms(1), borderColor: '#d8dbdd' },
  textArea: { minHeight: s(80), textAlignVertical: 'top' },
  addButton: { backgroundColor: '#29BFC0', borderRadius: ms(25), paddingVertical: ms(12), paddingHorizontal: ms(30), alignItems: 'center', marginVertical: ms(20) },
  addButtonText: { color: '#FFFFFF', fontSize: ms(16, 0.3), fontWeight: '600' },
  addedBeneficiaries: { marginTop: ms(10) },
  beneficiarySection: { marginBottom: ms(16) },
  beneficiarySectionTitle: { fontSize: ms(16, 0.3), fontWeight: '600', color: '#29BFC0', marginBottom: ms(8) },
  beneficiaryItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#FFFFFF', borderRadius: ms(12), padding: ms(12), marginBottom: ms(8) },
  beneficiaryInfo: { flex: 1 },
  beneficiaryName: { fontSize: ms(16, 0.3), fontWeight: '600', color: '#1C2C4F' },
  beneficiaryDetails: { fontSize: ms(14, 0.3), color: '#6B7280', marginTop: ms(2) },
  removeButton: { color: '#FF6B6B', fontSize: ms(14, 0.3), fontWeight: '600' },
  submitButtonContainer: { alignItems: 'center', marginTop: ms(20), marginBottom: ms(40) },
  saveButton: { backgroundColor: '#29BFC0', borderRadius: ms(25), paddingVertical: ms(15), paddingHorizontal: ms(60), alignItems: 'center', marginBottom: ms(120), minWidth: s(200) },
  saveButtonText: { color: '#FFFFFF', fontSize: ms(16, 0.3), fontWeight: '600' },
  bottomPadding: { height: s(120) },
});
